import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Grid,
  MenuItem,
  Box,
  Chip,
  Autocomplete,
  IconButton,
  Divider,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import { Icon } from "@iconify/react";

import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import Http from "../../../services/Http";
import dataService from "../../../services/data.service";
import LoadingScreen from "../../common/LoadingScreen";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useHMG } from "../../../HMGContext";
import { formatPlanComparisonLabel } from "../../../pages/edit-plans/utils/planComparisonLabelUtils";
import {
  adaptComparisons,
  buildComparisonSaveRequest,
  defaultComparisonName,
} from "../../../pages/edit-plans/utils/planComparisonModel";
import {
  MAX_COMPARISONS,
  COMPARISON_TARGET,
  COMPARISON_DATA_TYPE,
  COMPARISON_PLACEMENT_LABELS,
} from "../../../constants/keywords";

const selectFieldProps = { IconComponent: KeyboardArrowDownIcon };

/** Format a YYYYMM integer (e.g. 202608) as "MMM YYYY" (e.g. "Aug 2026"). Null on invalid input. */
const formatClosedMonthLabel = (yyyymm) => {
  if (yyyymm == null) return null;
  const year = Math.floor(yyyymm / 100);
  const month = yyyymm % 100;
  if (!year || month < 1 || month > 12) return null;
  return dayjs().year(year).month(month - 1).format("MMM YYYY");
};

const PLAN_TYPE_LIST = [
  { label: "Budget", value: "Budget" },
  { label: "What If", value: "What if" },
  { label: "Forecast", value: "Forecast" },
];

/** A single blank comparison row seeded with the current plan's property / year / plan type. */
const makeEmptyRow = (index, planDetails) => ({
  name: defaultComparisonName(index),
  comparisonTarget: COMPARISON_TARGET.ACTUALS,
  propertyId: planDetails?.propertyIds?.[0] ?? null,
  year: planDetails?.fiscalYear ? String(planDetails.fiscalYear) : null,
  planType: planDetails?.planType ?? "",
  versionName: null,
  versionNumber: null,
  asOnDate: null,
});

// Per-row schema — mirrors the legacy single-comparison validation, applied per row.
const rowSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required("Comparison name is required")
    .max(100, "Max 100 characters")
    .test("unique-name", "Comparison name must be unique", function (value) {
      // `this.from[1]` is the parent Formik values object (the row is `this.from[0]`).
      const all = this.from?.[1]?.value?.comparisons || [];
      if (!value) return true;
      const norm = value.trim().toLowerCase();
      const matches = all.filter((c) => (c?.name || "").trim().toLowerCase() === norm).length;
      return matches <= 1;
    }),
  comparisonTarget: Yup.string().required("Comparison target is required"),
  propertyId: Yup.mixed()
    .nullable()
    .test("property-required", "Property is required", (value, ctx) => {
      const { comparisonTarget } = ctx.parent;
      // TPX-1902: Trailing 12 month also requires a property (its own trailing-twelve of actuals).
      if (
        (comparisonTarget === COMPARISON_TARGET.ACTUALS ||
          comparisonTarget === COMPARISON_TARGET.SPECIFIC ||
          comparisonTarget === COMPARISON_TARGET.TRAILING_12) &&
        !value
      ) {
        return false;
      }
      return true;
    }),
  year: Yup.number()
    .nullable()
    .min(2000, "Year must be 2000 or greater")
    .test("year-required", "Year is required", function (value) {
      const { comparisonTarget } = this.parent;
      if (
        (comparisonTarget === COMPARISON_TARGET.ACTUALS ||
          comparisonTarget === COMPARISON_TARGET.SPECIFIC) &&
        !value
      ) {
        return false;
      }
      return true;
    }),
  planType: Yup.string()
    .nullable()
    .test("plan-type-required", "Plan type is required", (value, ctx) => {
      const { comparisonTarget } = ctx.parent;
      if (
        (comparisonTarget === COMPARISON_TARGET.SPECIFIC ||
          comparisonTarget === COMPARISON_TARGET.SNAPSHOT) &&
        !value
      ) {
        return false;
      }
      return true;
    }),
  versionName: Yup.string()
    .nullable()
    .test("version-required", "Version name is required", function (value) {
      const { comparisonTarget } = this.parent;
      if (comparisonTarget !== COMPARISON_TARGET.SPECIFIC) return true;
      return !!value;
    }),
  asOnDate: Yup.string()
    .nullable()
    .test("snapshot-date-required", "Snapshot date is required", function (value) {
      const { comparisonTarget } = this.parent;
      const isMissing = !value || value === "null" || value === "";
      if (comparisonTarget === COMPARISON_TARGET.SNAPSHOT && isMissing) return false;
      return true;
    }),
});

const validationSchema = Yup.object({
  comparisons: Yup.array().of(rowSchema).min(1).max(MAX_COMPARISONS),
  isPercentageView: Yup.string(),
  showInHighlights: Yup.boolean(),
  showInRollups: Yup.boolean(),
  showInPlanTable: Yup.boolean(),
});

/**
 * One comparison row inside the FieldArray. Owns its own version list so per-row Specific-plan
 * lookups are independent (a fetch failure clears only this row's version). All fields are bound to
 * `comparisons[index].*`.
 */
const ComparisonRow = ({
  index,
  row,
  errors,
  touched,
  setFieldValue,
  propertyOptions,
  onDelete,
  canDelete,
  showToast,
  planPropertyId,
  closedMonthByProperty,
  resolveClosedMonthForProperty,
}) => {
  const base = `comparisons[${index}]`;
  const rowErrors = errors?.comparisons?.[index] || {};
  const rowTouched = touched?.comparisons?.[index] || {};

  const [versionList, setVersionList] = useState([]);
  const [isVersionDisabled, setIsVersionDisabled] = useState(true);

  const isActuals = row.comparisonTarget === COMPARISON_TARGET.ACTUALS;
  const isSpecific = row.comparisonTarget === COMPARISON_TARGET.SPECIFIC;
  const isSnapshot = row.comparisonTarget === COMPARISON_TARGET.SNAPSHOT;
  const isTrailing12 = row.comparisonTarget === COMPARISON_TARGET.TRAILING_12;
  // TPX-1902: closed month is resolved for THIS row's selected property (each property has its own
  // trailing-twelve anchor). Absent → this Trailing 12 month row blocks Save.
  const rowClosedMonth =
    isTrailing12 && row.propertyId != null ? closedMonthByProperty?.[row.propertyId] : undefined;
  const trailing12Unresolved =
    isTrailing12 && rowClosedMonth?.loaded && rowClosedMonth?.yyyymm == null;

  // TPX-1902: resolve the selected property's effective closed month whenever it changes.
  useEffect(() => {
    if (isTrailing12 && row.propertyId != null) {
      resolveClosedMonthForProperty(row.propertyId);
    }
  }, [isTrailing12, row.propertyId, resolveClosedMonthForProperty]);

  // Fetch versions for a Specific-plan row (independent per row).
  const fetchVersions = async (propertyId, year, planType) => {
    if (!propertyId || !year || !planType || year.toString().length !== 4) {
      setVersionList([]);
      setIsVersionDisabled(true);
      return;
    }
    try {
      setIsVersionDisabled(true);
      const res = await Http.GET(`plan/${propertyId}/${year}/${planType}`);
      if (res && res.status === 200 && res.data.length > 0) {
        setVersionList(
          res.data.map((item) => ({
            value: item.value,
            fiscalYear: item.fiscalYear,
            versionName: item.versionName,
            versionNumber: item.versionNumber,
            planStatus: item.planStatus,
          }))
        );
        setIsVersionDisabled(false);
      } else {
        setVersionList([]);
        setIsVersionDisabled(true);
      }
    } catch (error) {
      setVersionList([]);
      setIsVersionDisabled(true);
      showToast("Error fetching version names.", "error");
    }
  };

  // Prefill: load versions when the row starts as a Specific plan with all keys present.
  useEffect(() => {
    if (isSpecific && row.propertyId && row.year && row.planType) {
      fetchVersions(row.propertyId, row.year, row.planType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTargetChange = (e) => {
    const target = e.target.value;
    setFieldValue(`${base}.comparisonTarget`, target);
    if (target === COMPARISON_TARGET.SNAPSHOT) {
      setFieldValue(`${base}.propertyId`, null);
      setFieldValue(`${base}.year`, null);
      setFieldValue(`${base}.versionName`, null);
      setFieldValue(`${base}.versionNumber`, null);
    }
    if (target === COMPARISON_TARGET.TRAILING_12) {
      // TPX-1902: user picks the property to compare against (defaults to the plan's own; a prior
      // selection is kept). The window is derived server-side from that property's closed month, so the
      // year / plan type / version / as-of selectors are cleared. Name keeps the generic "Comparison N".
      setFieldValue(`${base}.propertyId`, row.propertyId ?? planPropertyId);
      setFieldValue(`${base}.year`, null);
      setFieldValue(`${base}.planType`, "");
      setFieldValue(`${base}.versionName`, null);
      setFieldValue(`${base}.versionNumber`, null);
      setFieldValue(`${base}.asOnDate`, null);
    }
  };

  const clearVersion = () => {
    setFieldValue(`${base}.versionName`, null);
    setFieldValue(`${base}.versionNumber`, null);
  };

  // Fixed-width flex fields so each target type shows exactly the Figma fields (no stray column) and
  // columns line up across rows. Only the fields relevant to the selected target are rendered.
  const fieldSx = {
    ".MuiInputLabel-root": { fontSize: "14px" },
    ".MuiOutlinedInput-root": { fontSize: "14px", height: "40px" },
  };
  const selectSx = {
    ...fieldSx,
    ".MuiSelect-select": { fontSize: "14px", pr: "36px" },
    ".MuiSelect-icon": { color: "#637381", right: "10px" },
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1, minWidth: 0 }}>
        {/* Comparison name */}
        <Box sx={{ width: 160 }}>
          <TextField
            fullWidth
            size="small"
            label="Comparison name*"
            value={row.name || ""}
            onChange={(e) => setFieldValue(`${base}.name`, e.target.value)}
            error={rowTouched.name && Boolean(rowErrors.name)}
            helperText={rowTouched.name && rowErrors.name}
            sx={fieldSx}
          />
        </Box>

        {/* Target */}
        <Box sx={{ width: 150 }}>
          <TextField
            fullWidth
            select
            size="small"
            SelectProps={selectFieldProps}
            value={row.comparisonTarget || ""}
            onChange={handleTargetChange}
            sx={selectSx}
          >
            <MenuItem value={COMPARISON_TARGET.ACTUALS}>Actuals</MenuItem>
            <MenuItem value={COMPARISON_TARGET.SPECIFIC}>Specific plan</MenuItem>
            <MenuItem value={COMPARISON_TARGET.SNAPSHOT}>Snapshot</MenuItem>
            {/* TPX-1902: always enabled, even when no closed month is configured for the property. */}
            <MenuItem value={COMPARISON_TARGET.TRAILING_12}>Trailing 12 month</MenuItem>
          </TextField>
        </Box>

        {/* Property (actuals + specific + trailing 12 month) */}
        {(isActuals || isSpecific || isTrailing12) && (
          <Box sx={{ width: 210 }}>
            <Autocomplete
              fullWidth
              disablePortal
              popupIcon={<KeyboardArrowDownIcon />}
              size="small"
              options={propertyOptions || []}
              value={propertyOptions?.find((o) => o.value === row.propertyId) || null}
              getOptionLabel={(option) => option?.label ?? ""}
              isOptionEqualToValue={(a, b) => a?.value === b?.value}
              noOptionsText="No properties available"
              onChange={(event, newValue) => {
                const propertyId = newValue?.value ?? null;
                setFieldValue(`${base}.propertyId`, propertyId);
                if (isSpecific) {
                  clearVersion();
                  fetchVersions(propertyId, row.year, row.planType);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Property*"
                  variant="outlined"
                  error={rowTouched.propertyId && Boolean(rowErrors.propertyId)}
                  helperText={rowTouched.propertyId && rowErrors.propertyId}
                  sx={{ ".MuiInputLabel-root": { fontSize: "14px" }, ".MuiOutlinedInput-root": { fontSize: "14px" } }}
                />
              )}
            />
          </Box>
        )}

        {/* Year (actuals + specific) */}
        {(isActuals || isSpecific) && (
          <Box sx={{ width: 120 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Year*"
                views={["year"]}
                value={row.year ? dayjs().year(parseInt(row.year, 10)) : null}
                onChange={(newValue) => {
                  const value = newValue ? newValue.format("YYYY") : null;
                  setFieldValue(`${base}.year`, value);
                  if (isSpecific) {
                    clearVersion();
                    fetchVersions(row.propertyId, value, row.planType);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    inputProps: { maxLength: 4 },
                    sx: {
                      ".MuiInputLabel-root": { fontSize: "14px !important" },
                      ".MuiOutlinedInput-root": { fontSize: "14px !important", height: "40px" },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* Plan type (specific + snapshot) */}
        {(isSpecific || isSnapshot) && (
          <Box sx={{ width: 150 }}>
            <TextField
              fullWidth
              label="Plan type*"
              variant="outlined"
              size="small"
              select
              SelectProps={selectFieldProps}
              value={row.planType || ""}
              onChange={(e) => {
                const val = e.target.value;
                setFieldValue(`${base}.planType`, val);
                if (isSpecific) {
                  clearVersion();
                  fetchVersions(row.propertyId, row.year, val);
                }
              }}
              error={rowTouched.planType && Boolean(rowErrors.planType)}
              helperText={rowTouched.planType && rowErrors.planType}
              sx={selectSx}
            >
              {PLAN_TYPE_LIST.filter((v) => !(isSnapshot && v.value === "What if")).map((v, i) => (
                <MenuItem style={{ fontSize: "12px" }} value={v.value} key={`pt-${index}-${i}`}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {/* Version (specific) */}
        {isSpecific && (
          <Box sx={{ width: 220 }}>
            <Autocomplete
              fullWidth
              disablePortal
              popupIcon={<KeyboardArrowDownIcon />}
              size="small"
              options={versionList || []}
              value={versionList.find((v) => v.versionName === row.versionName) || null}
              getOptionLabel={(option) =>
                option ? `${option.fiscalYear}-V${option.versionNumber}-${option.versionName}` : ""
              }
              isOptionEqualToValue={(a, b) => a?.versionName === b?.versionName}
              noOptionsText="No versions available"
              disabled={isVersionDisabled}
              onChange={(event, newValue) => {
                setFieldValue(`${base}.versionName`, newValue?.versionName ?? null);
                setFieldValue(`${base}.versionNumber`, newValue?.value ?? null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Version name*"
                  variant="outlined"
                  error={rowTouched.versionName && Boolean(rowErrors.versionName)}
                  helperText={rowTouched.versionName && rowErrors.versionName}
                  sx={{ ".MuiInputLabel-root": { fontSize: "14px" }, ".MuiOutlinedInput-root": { fontSize: "14px" } }}
                />
              )}
            />
          </Box>
        )}

        {/* As-of date (snapshot) */}
        {isSnapshot && (
          <Box sx={{ width: 170 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="As of date*"
                value={row.asOnDate && row.asOnDate !== "null" ? dayjs(row.asOnDate) : null}
                onChange={(newValue) => {
                  setFieldValue(`${base}.asOnDate`, newValue ? newValue.format("YYYY-MM-DD") : null);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: rowTouched.asOnDate && Boolean(rowErrors.asOnDate),
                    helperText: rowTouched.asOnDate && rowErrors.asOnDate,
                    sx: {
                      ".MuiInputLabel-root": { fontSize: "14px" },
                      ".MuiOutlinedInput-root": { fontSize: "14px", height: "40px" },
                    },
                    InputProps: { readOnly: true },
                  },
                  field: { readOnly: true },
                  actionBar: { actions: ["clear", "accept"] },
                }}
              />
            </LocalizationProvider>
          </Box>
        )}

        {/* TPX-1902: Trailing 12 month — read-only closed-month label for the SELECTED property + no-data warning. */}
        {isTrailing12 && (
          <Box sx={{ display: "flex", alignItems: "center", minHeight: "40px", flex: 1, minWidth: 220 }}>
            {row.propertyId == null ? (
              <Typography sx={{ fontSize: "14px", color: "#637381" }}>
                Select a property to compare against.
              </Typography>
            ) : !rowClosedMonth?.loaded ? (
              <Typography sx={{ fontSize: "14px", color: "#637381" }}>
                Resolving closed accounting month…
              </Typography>
            ) : trailing12Unresolved ? (
              <Typography sx={{ fontSize: "13px", color: "#B71D18", lineHeight: 1.3 }}>
                No closed accounting month is configured for this property — a Trailing 12 month
                comparison cannot be saved.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "14px", color: "#212B36" }}>
                Closed accounting month: <b>{rowClosedMonth.label}</b>
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Delete row (disabled when only one row remains) — app-standard trash icon */}
      <IconButton
        aria-label="Delete comparison"
        onClick={onDelete}
        disabled={!canDelete}
        sx={{ mt: 0.5 }}
      >
        <Icon
          icon="solar:trash-bin-trash-bold"
          width={18}
          height={18}
          color={canDelete ? "#FF5630" : "#919EAB"}
        />
      </IconButton>
    </Box>
  );
};

const ConfigureComparisonDialog = ({
  open,
  handleClose,
  planId,
  setUpdatePlanDetailsPage,
  showToast,
  setShowVarianceInRollups,
  setShowVarianceInHighlights,
  planDetails,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    setCompareHighLightLoading,
    setPlanDetailsRollUpLoader,
    setStopLoading,
    setShowPlanTableVarience,
    setPlanTableRefreshTrigger,
    setHeaderComparisonPlanLabel,
    setPlanComparisons,
    setHeaderComparisonLabels,
    setSelectedComparisonIds,
  } = useHMG();

  const propertyOptions = dataService.getPropertiesForHMG();

  // TPX-1902: the plan's own property is the default for a Trailing 12 month row, but the user may pick
  // any property to compare against. Each property's effective closed accounting month (property override
  // → org fallback) is resolved on demand and cached here by propertyId — used for the read-only label
  // and the Save-gate.
  const planPropertyId = planDetails?.propertyIds?.[0] ?? null;
  const orgId = planDetails?.organizationId ?? dataService.getSelectedHMGId();
  const [closedMonthByProperty, setClosedMonthByProperty] = useState({});
  const closedMonthRequested = useRef(new Set());

  const resolveClosedMonthForProperty = useCallback(
    (propertyId) => {
      if (propertyId == null || !orgId) return;
      if (closedMonthRequested.current.has(propertyId)) return;
      closedMonthRequested.current.add(propertyId);
      setClosedMonthByProperty((prev) => ({
        ...prev,
        [propertyId]: { loaded: false, yyyymm: null, label: null },
      }));
      dataService
        .getResolvedClosedMonth(orgId, propertyId)
        .then((data) => {
          const yyyymm = data?.closedAccountingMonth ?? null;
          setClosedMonthByProperty((prev) => ({
            ...prev,
            [propertyId]: { loaded: true, yyyymm, label: formatClosedMonthLabel(yyyymm) },
          }));
        })
        .catch(() => {
          setClosedMonthByProperty((prev) => ({
            ...prev,
            [propertyId]: { loaded: true, yyyymm: null, label: null },
          }));
        });
    },
    [orgId]
  );

  // Reset the resolved-closed-month cache when the dialog closes, so the next open re-resolves fresh.
  // (Resetting on close — not open — avoids clobbering the placeholders the row effects set on mount,
  // since React runs child effects before this parent effect.)
  useEffect(() => {
    if (!open) {
      closedMonthRequested.current = new Set();
      setClosedMonthByProperty({});
    }
  }, [open]);

  const [initialValues, setInitialValues] = useState({
    comparisons: [makeEmptyRow(0, planDetails)],
    isPercentageView: "percentage",
    showInHighlights: true,
    showInRollups: true,
    showInPlanTable: true,
  });

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    Http.GET("plan-comparison/" + planId)
      .then((res) => {
        if (res && res.status === 200) {
          const adapted = adaptComparisons(res.data, planDetails);
          const rows =
            adapted.length > 0
              ? adapted.map((c) => ({
                  name: c.name,
                  comparisonTarget: c.comparisonTarget,
                  propertyId: c.compareToPropertyId,
                  year: c.year != null ? String(c.year) : null,
                  planType: c.planType || "",
                  versionName: c.versionName,
                  versionNumber: c.compareToPlanId,
                  asOnDate: c.snapshotDate,
                }))
              : [makeEmptyRow(0, planDetails)];

          setInitialValues({
            comparisons: rows,
            isPercentageView: res.data?.isPercentageView ? "percentage" : "numerical",
            showInHighlights: res.data?.showInHighlights == null ? true : res.data.showInHighlights,
            showInRollups: res.data?.showInRollups == null ? true : res.data.showInRollups,
            showInPlanTable: res.data?.showInPlanTable == null ? true : res.data.showInPlanTable,
          });
        } else {
          handleClose();
          showToast("Error in fetching plan comparison details.", "error");
        }
      })
      .catch(() => {
        handleClose();
        showToast("Error in fetching plan comparison details.", "error");
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planDetails]);

  const handleSubmit = (values) => {
    // Map Formik rows → canonical comparisons, then build the verified save request.
    const comparisons = values.comparisons.map((r) => ({
      name: (r.name || "").trim(),
      comparisonTarget: r.comparisonTarget,
      compareToPropertyId: r.propertyId,
      year: r.year,
      planType: r.planType || null,
      compareToPlanId: r.versionNumber != null ? Number(r.versionNumber) : null,
      versionName: r.versionName || null,
      snapshotDate: r.asOnDate || null,
    }));

    const placement = {
      showInHighlights: !!values.showInHighlights,
      showInRollups: !!values.showInRollups,
      showInPlanTable: !!values.showInPlanTable,
    };
    const isPercentageView = values.isPercentageView === "percentage";
    const sendResult = buildComparisonSaveRequest({ planId, comparisons, placement, isPercentageView });

    setIsLoading(true);
    setCompareHighLightLoading(true);
    setPlanDetailsRollUpLoader(true);
    setStopLoading(false);
    Http.POST("plan-comparison/create-update-planComparison", sendResult)
      .then((res) => {
        setIsLoading(false);
        if (res && res.status === 201) {
          handleClose();
          // Placement flags → existing surface gates (back-compat).
          setShowVarianceInHighlights(placement.showInHighlights);
          setShowVarianceInRollups(placement.showInRollups);
          setShowPlanTableVarience(placement.showInPlanTable);

          // Multi-comparison state: canonical list, header labels, and reset the Filters selection
          // to "all" (null) so newly-configured comparisons are visible by default.
          setPlanComparisons(comparisons);
          setSelectedComparisonIds(null);
          const labels = comparisons.map((c) => ({
            name: c.name,
            summary: formatPlanComparisonLabel(
              {
                dataType:
                  c.comparisonTarget === COMPARISON_TARGET.ACTUALS
                    ? "Actuals"
                    : c.comparisonTarget === COMPARISON_TARGET.SNAPSHOT
                      ? "Snapshot"
                      : c.comparisonTarget === COMPARISON_TARGET.TRAILING_12
                        ? COMPARISON_DATA_TYPE.TRAILING_12
                        : "Plan",
                planType: c.planType,
                year: c.year,
                compareToPropertyId: c.compareToPropertyId,
                versionName: c.versionName,
                snapShotDate: c.snapshotDate,
                // TPX-1902: header/tooltip "as of" suffix — the SELECTED property's closed month.
                closedMonthLabel:
                  c.comparisonTarget === COMPARISON_TARGET.TRAILING_12
                    ? closedMonthByProperty[c.compareToPropertyId]?.label
                    : undefined,
              },
              planDetails
            ),
          }));
          setHeaderComparisonLabels(labels);
          setHeaderComparisonPlanLabel(labels[0]?.summary ?? null);

          showToast("Comparison data updated.", "success");
          setCompareHighLightLoading(false);
          setPlanDetailsRollUpLoader(false);
          setPlanTableRefreshTrigger(new Date());
          setTimeout(() => setUpdatePlanDetailsPage(new Date()), 1000);
        } else {
          showToast("Failed to update the comparison data", "error");
        }
      })
      .catch(() => {
        setIsLoading(false);
        setCompareHighLightLoading(false);
        setPlanDetailsRollUpLoader(false);
        showToast("Failed to update the comparison data", "error");
      });
  };

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ padding: "16px 16px 10px 16px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h4" sx={{ fontSize: "18px", fontWeight: 700, color: "#212B36" }}>
            Configure comparison(s)
          </Typography>
          <IconButton onClick={handleClose}>
            <ClearIcon sx={{ width: "16px", height: "16px" }} />
          </IconButton>
        </Box>
        <Typography sx={{ color: "#637381", fontSize: "12px", fontWeight: 400 }}>
          *Required fields
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ padding: "0px 16px 16px 16px" }}>
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, setFieldValue, isValid, dirty }) => {
              // TPX-1902: block Save when any Trailing 12 month row's SELECTED property resolved to no
              // closed accounting month — that comparison's window cannot be derived.
              const trailing12Blocked = values.comparisons.some((c) => {
                if (c.comparisonTarget !== COMPARISON_TARGET.TRAILING_12 || c.propertyId == null) return false;
                const info = closedMonthByProperty[c.propertyId];
                return info?.loaded && info.yyyymm == null;
              });
              return (
              <Form>
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#212B36", lineHeight: "24px", mb: 1 }}
                >
                  What do you want to compare to?
                </Typography>

                <FieldArray name="comparisons">
                  {({ push, remove }) => (
                    <>
                      {values.comparisons.map((row, index) => (
                        <ComparisonRow
                          key={index}
                          index={index}
                          row={row}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          propertyOptions={propertyOptions}
                          canDelete={values.comparisons.length > 1}
                          onDelete={() => remove(index)}
                          showToast={showToast}
                          planPropertyId={planPropertyId}
                          closedMonthByProperty={closedMonthByProperty}
                          resolveClosedMonthForProperty={resolveClosedMonthForProperty}
                        />
                      ))}

                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        size="small"
                        disabled={values.comparisons.length >= MAX_COMPARISONS}
                        onClick={() => push(makeEmptyRow(values.comparisons.length, planDetails))}
                        sx={{ mt: 1, color: "#212B36", borderColor: "#919EAB52", textTransform: "none" }}
                      >
                        Comparison
                      </Button>
                    </>
                  )}
                </FieldArray>

                <Divider sx={{ my: 2 }} />
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#212B36", lineHeight: "24px", mb: 1 }}
                >
                  How do you want to display comparisons?
                </Typography>
                <RadioGroup
                  value={values.isPercentageView || "percentage"}
                  onChange={(e) => setFieldValue("isPercentageView", e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel
                    value="numerical"
                    control={<Radio size="small" />}
                    label="Show as numerical difference"
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "14px", color: "#212B36" } }}
                  />
                  <FormControlLabel
                    value="percentage"
                    control={<Radio size="small" />}
                    label="Show as percentage"
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "14px", color: "#212B36" } }}
                  />
                </RadioGroup>

                <Divider sx={{ my: 2 }} />
                <Typography
                  sx={{ fontSize: "14px", fontWeight: 600, color: "#212B36", lineHeight: "24px", mb: 1 }}
                >
                  Where do you want to see the comparisons?
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="showInHighlights"
                        color="primary"
                        checked={!!values.showInHighlights}
                        onChange={(e) => setFieldValue("showInHighlights", e.target.checked)}
                      />
                    }
                    label={COMPARISON_PLACEMENT_LABELS.HIGHLIGHTS}
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "14px !important", color: "#212B36" } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="showInRollups"
                        color="primary"
                        checked={!!values.showInRollups}
                        onChange={(e) => setFieldValue("showInRollups", e.target.checked)}
                      />
                    }
                    label={COMPARISON_PLACEMENT_LABELS.ROLLUPS}
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "14px !important", color: "#212B36" } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="showInPlanTable"
                        color="primary"
                        checked={!!values.showInPlanTable}
                        onChange={(e) => setFieldValue("showInPlanTable", e.target.checked)}
                      />
                    }
                    label={COMPARISON_PLACEMENT_LABELS.PLAN_TABLE}
                    sx={{ ".MuiFormControlLabel-label": { fontSize: "14px !important", color: "#212B36" } }}
                  />
                </Box>

                <DialogActions sx={{ padding: "0px", marginTop: "16px" }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    size="medium"
                    sx={{ color: "#212B36", borderColor: "#919EAB52" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!dirty || !isValid || trailing12Blocked}
                    size="medium"
                  >
                    Configure comparison
                  </Button>
                </DialogActions>
              </Form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConfigureComparisonDialog;
